import {Action} from '@reduxjs/toolkit'
import {put, takeLatest, select, call} from 'redux-saga/effects'
import {getAgentsByPage, createAgent, updateAgent, deleteAgent} from './AgentsCRUD'
import {AgentModel} from '../models/AgentModel'

export interface ActionWithPayload<T> extends Action {
  payload?: T
}

export const actionTypes = {
  // Fetch via API
  FetchPage: '[Agents] FetchPage Action',
  // Set the page fetched via API
  SetPage: '[Agents] SetPage Action',
  // Create new agent
  CreateAgent: '[Agents] CreateAgent Action',
  // Update agent
  UpdateAgent: '[Agents] UpdateAgent Action',
  // Delete agent
  DeleteAgent: '[Agents] DeleteAgent Action',
}

const initialAgentsState: IAgentsState = {
  currentPage: 1,
  agents: [],
  totalAgents: 0,
  agentsPerPage: 10,
  visiblePageList: [],
  totalPages: 0,
  isLoading: false,
  error: null,
}

export interface IAgentsState {
  currentPage: number
  totalAgents: number
  agents: AgentModel[]
  agentsPerPage: number
  visiblePageList: number[]
  totalPages: number
  isLoading: boolean
  error: string | null
}

export const reducer = (
  state: IAgentsState = initialAgentsState,
  action: ActionWithPayload<any>
) => {
  switch (action.type) {
    case actionTypes.SetPage: {
      return {...state, ...action.payload, isLoading: false, error: null}
    }
    default:
      return state
  }
}

export const actions = {
  fetchPage: (currentPage: number) => ({type: actionTypes.FetchPage, payload: {currentPage}}),
  setPage: (
    agents: AgentModel[],
    currentPage: number,
    totalAgents: number,
    visiblePageList: number[],
    totalPages: number
  ) => ({
    type: actionTypes.SetPage,
    payload: {agents, currentPage, totalAgents, visiblePageList, totalPages, isLoading: false},
  }),
  createAgent: (name: string, email: string, password: string) => ({
    type: actionTypes.CreateAgent,
    payload: {name, email, password},
  }),
  updateAgent: (agentId: string, data: any) => ({
    type: actionTypes.UpdateAgent,
    payload: {agentId, data},
  }),
  deleteAgent: (agentId: string) => ({
    type: actionTypes.DeleteAgent,
    payload: {agentId},
  }),
}

export function* saga() {
  yield takeLatest(
    actionTypes.CreateAgent,
    function* createAgentHandler(action: ActionWithPayload<any>) {
      try {
        // @ts-ignore
        const stateSelectFn = (state) => {
          return {
            subscriberId: state.auth.user.subscriberId,
          }
        }

        // @ts-ignore
        let selectedState: any = yield select(stateSelectFn)

        yield createAgent(selectedState.subscriberId, {
          name: action.payload.name,
          email: action.payload.email,
          password: action.payload.password,
        })

        // Refresh the agents list after creating a new agent
        yield put(actions.fetchPage(1))
      } catch (error) {
        console.error('Error creating agent:', error)
      }
    }
  )

  yield takeLatest(
    actionTypes.UpdateAgent,
    function* updateAgentHandler(action: ActionWithPayload<any>) {
      try {
        // @ts-ignore
        const stateSelectFn = (state) => {
          return {
            subscriberId: state.auth.user.subscriberId,
          }
        }

        // @ts-ignore
        let selectedState: any = yield select(stateSelectFn)

        yield updateAgent(selectedState.subscriberId, action.payload.agentId, action.payload.data)

        // Refresh the agents list after updating an agent
        yield put(actions.fetchPage(1))
      } catch (error) {
        console.error('Error updating agent:', error)
      }
    }
  )

  yield takeLatest(
    actionTypes.DeleteAgent,
    function* deleteAgentHandler(action: ActionWithPayload<any>) {
      try {
        // @ts-ignore
        const stateSelectFn = (state) => {
          return {
            subscriberId: state.auth.user.subscriberId,
          }
        }

        // @ts-ignore
        let selectedState: any = yield select(stateSelectFn)

        yield deleteAgent(selectedState.subscriberId, action.payload.agentId)

        // Refresh the agents list after deleting an agent
        yield put(actions.fetchPage(1))
      } catch (error) {
        console.error('Error deleting agent:', error)
      }
    }
  )

  yield takeLatest(actionTypes.FetchPage, function* pageRequested(action: ActionWithPayload<any>) {
    try {
      // @ts-ignore
      const stateSelectFn = (state) => {
        return {
          subscriberId: state.auth.user.subscriberId,
          agentsPerPage: state.agents?.agentsPerPage || 10,
          visiblePageList: state.agents?.visiblePageList || [],
        }
      }

      // @ts-ignore
      let requestedPage: number = action.payload.currentPage
      let selectedState: any = yield select(stateSelectFn)
      const {data: agents, headers} = yield getAgentsByPage(
        selectedState.subscriberId,
        requestedPage
      )

      let totalAgents: number = parseInt(headers['x-total-count'] || '0')
      let agentsPerPage: number = selectedState.agentsPerPage
      let totalPages: number =
        Math.floor(totalAgents / agentsPerPage) +
        Math.ceil((totalAgents % agentsPerPage) / agentsPerPage)

      let visiblePageList: number[] = []

      let minVisible: number = 1
      let maxVisible: number = Math.min(5, totalPages)

      // Update visible page list
      if (
        requestedPage < selectedState.visiblePageList[0] ||
        selectedState.visiblePageList.length === 0
      ) {
        minVisible = requestedPage
        maxVisible = Math.min(minVisible + 4, totalPages)
      } else if (
        requestedPage > selectedState.visiblePageList[selectedState.visiblePageList.length - 1]
      ) {
        maxVisible = Math.min(requestedPage, totalPages)
        minVisible = Math.max(1, requestedPage - 4)
      }

      for (let index = minVisible; index <= maxVisible; index++) {
        visiblePageList.push(index)
      }

      yield put(
        actions.setPage(
          agents,
          action.payload.currentPage,
          totalAgents,
          visiblePageList,
          totalPages
        )
      )
    } catch (error) {
      console.error('Error fetching agents:', error)
    }
  })
}
